import { array } from '@ember/helper';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismIcon,
  PrismMetricBlock,
  PrismTooltipHeader,
} from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t, tKey } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import GeneralTooltipsTooltipEvents from 'lumen/components/general/tooltips/tooltip-events';
import HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightDifferenceRow from 'lumen/components/hotel-and-market/revenue-insight/tables/rows/tooltip-revenue-insight-difference-row';
import HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow from 'lumen/components/hotel-and-market/revenue-insight/tables/rows/tooltip-revenue-insight-row';
import {
  compsetDifference,
  compsetDifferenceBackground,
} from 'lumen/utils/compset-difference';
import {
  NORMALIZED_PERIOD,
  REVENUE_INSIGHT_METRICS,
} from 'lumen/utils/constants/ri-constant-values';
import findMedianRate from 'lumen/utils/numeric/median';
import roundSegmentationValue from 'lumen/utils/revenue-insight/round-segmentation-value';

interface HotelAndMarketRevenueInsightTooltipsTooltipStayDateSignature {
  Args: {
    aggregationPeriod: unknown;
    data: unknown;
    hideTarget: unknown;
    isGroupsSelected?: boolean;
    kpis: unknown;
    metricKey: unknown;
  };
}

export default class HotelAndMarketRevenueInsightTooltipsTooltipStayDate extends Component<HotelAndMarketRevenueInsightTooltipsTooltipStayDateSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];

  @computed(
    'args.data.rates',
    'ownRate.value',
    'hotelAndMarketService.hotel.hotelinfo.id',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetDifference() {
    const { data } = this.args;

    // @ts-expect-error: Incorrect type
    if (!data?.rates) {
      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.ownRate?.value > 0) {
      // @ts-expect-error: Incorrect type
      const { rates } = data;

      // Make an array of rates like: [145, 514, 89, ...] for the median util
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const ratesAsValuesArray = Object.keys(rates)
        .filter(
          (hotelinfoId) =>
            hotelinfoId !== this.hotelAndMarketService.hotel?.hotelinfo?.id &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            rates[hotelinfoId][0].value > 0,
        ) // median must not include own rate and have atleast some value
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        .map((hotelinfoId) => rates[hotelinfoId][0].value);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const median = findMedianRate(ratesAsValuesArray);

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      return compsetDifference(this.ownRate.value, median);
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetDifferenceBackground() {
    // @ts-expect-error: Incorrect type
    const { calendarColorLowerLimit, calendarColorUpperLimit } =
      this.hotelAndMarketService.hotel;

    return compsetDifferenceBackground(
      // @ts-expect-error: Incorrect type
      this.compsetDifference,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      calendarColorLowerLimit,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      calendarColorUpperLimit,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.ownRate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetDifferenceFormatted() {
    if (this.compsetDifference === 0) {
      // Don't show ± sign if not applicable
      return '0%';
    }

    if (this.compsetDifference !== null) {
      return this.intl.formatNumber(this.compsetDifference, {
        style: 'percent',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
        signDisplay: 'always',
      });
    }

    return this.intl.t('general.not_available_short');
  }

  @computed('args.data.day.events.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get events() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const events = this.args.data?.day?.events ?? [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-return
    return events.filter((event) => event.isAccepted);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedOwnRate() {
    if (this.ownRate) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return this.hotelAndMarketService.formatRateObject(this.ownRate);
    }

    return this.intl.t('general.not_available_short');
  }

  @computed('args.{data.calculatedValues.forecastReference,metricKey}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasForecastReference() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const forecastReferenceValue =
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.data?.calculatedValues?.forecastReference[this.args.metricKey];

    return (
      forecastReferenceValue !== null && forecastReferenceValue !== undefined
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasRates() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.access.hasRates && this.ownRate;
  }

  @computed('args.data.day.holidays')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get holidays() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.data?.day?.holidays;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isTargetActive() {
    return this.args.hideTarget ? false : this.riSelections.isTargetActive;
  }

  @computed('args.kpis.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiTitles() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.kpis.map((kpi) => this.intl.t(`revenue_insight.${kpi}`));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get lastYearMetrics() {
    const metrics = [REVENUE_INSIGHT_METRICS.REFERENCE];

    if (
      this.access.hasFeature('revenueinsight.cancellations') &&
      !this.showPickupLYinThisYear &&
      !this.riSelections.isTargetActive &&
      this.riSelections.isSingleProperty
    ) {
      // @ts-expect-error: Incorrect type
      metrics.push(REVENUE_INSIGHT_METRICS.CANCELLATION_REFERENCE_OR_FILTERED);
    }

    return metrics;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get lastYearValueMetrics() {
    const metrics = [...this.lastYearMetrics];

    if (this.showGrossValues) {
      // @ts-expect-error: Incorrect type
      metrics[0] = REVENUE_INSIGHT_METRICS.GROSS_REFERENCE;
    }

    return metrics;
  }

  @computed(
    'args.{data.rates,isGroupsSelected}',
    'hotelAndMarketService.hotel.hotelinfo.id',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownRate() {
    if (
      this.args.data &&
      // @ts-expect-error: Incorrect type
      this.args.data?.rates &&
      !this.args.isGroupsSelected
    ) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return this.args.data?.rates[
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.hotelAndMarketService.hotel?.hotelinfo?.id
      ][0];
    }

    return null;
  }

  @computed(
    'args.data.segmentationId',
    'riSelections.{isCancellationsOnlyView,isTargetActive,isSingleProperty}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showGrossValues() {
    const { isCancellationsOnlyView, isTargetActive, isSingleProperty } =
      this.riSelections;
    // @ts-expect-error: Incorrect type
    const isPickup = this.args.data?.segmentationId === RI_BREAKDOWNS.PICKUP;
    return (
      isSingleProperty &&
      (isCancellationsOnlyView || (isPickup && !isTargetActive))
    );
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPickupLY() {
    return (
      !this.riSelections.isCancellationsOnlyView && this.hasForecastReference
    );
  }

  @computed('showPickupLY', 'args.data.segmentationId')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPickupLYinThisYear() {
    return (
      // @ts-expect-error: Incorrect type
      this.showPickupLY && this.args.data?.segmentationId === RI_BREAKDOWNS.PACE
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get targetChange() {
    return [`${this.targetValue[0]}Change`];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get targetValue() {
    if (this.riSelections.isBudgetActive) {
      return ['budget'];
    }
    if (this.riSelections.isForecastActive) {
      return ['forecast'];
    }
    return [''];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get thisYearMetrics() {
    if (this.showPickupLYinThisYear) {
      return ['forecastReference'];
    }

    if (
      this.access.hasFeature('revenueinsight.cancellations') &&
      !this.riSelections.isTargetActive &&
      this.riSelections.isSingleProperty
    ) {
      return [
        REVENUE_INSIGHT_METRICS.BOB,
        REVENUE_INSIGHT_METRICS.CANCELLATION_BOB_OR_FILTERED,
      ];
    }

    return [REVENUE_INSIGHT_METRICS.BOB];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get thisYearValueMetrics() {
    const metrics = [...this.thisYearMetrics];

    if (this.showGrossValues && metrics[0] === REVENUE_INSIGHT_METRICS.BOB) {
      metrics[0] = REVENUE_INSIGHT_METRICS.GROSS_BOB;
    }

    return metrics;
  }

  /**
   * This generates the title for the tooltip
   * We can't simply use the helper because of the aggregation
   * returns the date or date range formatted
   */
  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.aggregationPeriod',
    'args.data.segmentationValueId',
    'dates.partialDateFormat.WEEKDAY_AND_DATE_LONG',
    'riSelections.dateRange',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tooltipTitle() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    let stayDate = dayjs.utc(this.args.data.segmentationValueId, 'YYYY-MM-DD');
    const format = this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG;

    if (this.args.aggregationPeriod === 'day') {
      return stayDate.format(format);
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const momentAggregagtion = NORMALIZED_PERIOD[this.args.aggregationPeriod];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    let aggregationDate = stayDate.endOf(momentAggregagtion);

    const { startDate, endDate } = this.riSelections.dateRange;

    // If the date if after the end date of the date selection, then use the end date
    if (aggregationDate.isAfter(endDate, 'day')) {
      aggregationDate = endDate;
    }

    // If the date if before the start date of the date selection, then use the start date
    if (stayDate.isBefore(startDate, 'day')) {
      stayDate = startDate;
    }

    return `${stayDate.format(format)} - ${aggregationDate.format(format)}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tooltipValues() {
    // @ts-expect-error: Incorrect type
    return roundSegmentationValue(this.args.data, false);
  }

  <template>
    <PrismTooltipHeader>
      {{this.tooltipTitle}}
    </PrismTooltipHeader>

    {{#if this.hasRates}}
      <div
        class="tooltip-section tooltip-section--width-auto grid auto-cols-fr grid-flow-col"
      >
        <PrismMetricBlock>
          <:value>
            <span
              class="rate--{{this.compsetDifferenceBackground}}"
            >{{this.formattedOwnRate}}</span>
          </:value>
          <:label>{{t "revenue_insight.own_bar_rate"}}</:label>
        </PrismMetricBlock>
        <PrismMetricBlock>
          <:value>{{this.compsetDifferenceFormatted}}</:value>
          <:label>{{t "revenue_insight.own_vs_compset"}}</:label>
        </PrismMetricBlock>
      </div>
    {{/if}}

    <table class="tooltip-revenue-insight">
      <thead>
        <tr>
          <th></th>
          <th></th>
          {{#each this.kpiTitles as |kpiTitle|}}
            <th class="text-200--bold">{{kpiTitle}}</th>
          {{/each}}
        </tr>
      </thead>

      <tbody>
        <tr>
          <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow
            {{! @glint-expect-error: Incorrect type }}
            @kpis={{@kpis}}
            {{! @glint-expect-error: Incorrect type }}
            @metrics={{this.thisYearMetrics}}
            @showPickupLY={{this.showPickupLYinThisYear}}
            {{! @glint-expect-error: Incorrect type }}
            @tooltipValues={{this.tooltipValues}}
            {{! @glint-expect-error: Incorrect type }}
            @valueMetrics={{this.thisYearValueMetrics}}
          />
        </tr>
        {{#if this.isTargetActive}}
          <tr>
            <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow
              {{! @glint-expect-error: Incorrect type }}
              @kpis={{@kpis}}
              {{! @glint-expect-error: Incorrect type }}
              @metrics={{this.targetValue}}
              {{! @glint-expect-error: Incorrect type }}
              @tooltipValues={{this.tooltipValues}}
            />
          </tr>
          <tr>
            <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow
              @isChange={{true}}
              {{! @glint-expect-error: Incorrect type }}
              @kpis={{@kpis}}
              {{! @glint-expect-error: Incorrect type }}
              @metrics={{this.targetChange}}
              {{! @glint-expect-error: Incorrect type }}
              @tooltipValues={{this.tooltipValues}}
            />
          </tr>
          {{#if this.showPickupLY}}
            <tr>
              <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow
                {{! @glint-expect-error: Incorrect type }}
                @kpis={{@kpis}}
                @metrics={{array "pickupLastYear"}}
                {{! @glint-expect-error: Incorrect type }}
                @tooltipValues={{this.tooltipValues}}
              />
            </tr>
          {{/if}}
        {{else}}
          <tr>
            <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow
              {{! @glint-expect-error: Incorrect type }}
              @kpis={{@kpis}}
              @metrics={{this.lastYearMetrics}}
              {{! @glint-expect-error: Incorrect type }}
              @tooltipValues={{this.tooltipValues}}
              @valueMetrics={{this.lastYearValueMetrics}}
            />
          </tr>
          {{#if this.riSelections.isBookingsOnlyView}}
            <tr>
              {{! @glint-expect-error: Incorrect type }}
              <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightDifferenceRow
                @kpis={{@kpis}}
                @metrics={{this.thisYearMetrics}}
                @tooltipValues={{this.tooltipValues}}
              />
            </tr>
          {{/if}}
        {{/if}}
      </tbody>
    </table>

    {{! @glint-expect-error: Incorrect type }}
    {{#if @data.isIncompletePeriod}}
      <div class="incomplete-period-row flex flex-row items-center">
        <PrismIcon @color="warning" @name="warning" @size="small" />
        <div class="ml-100">
          {{#if (eq @aggregationPeriod "week")}}
            {{t "general.incomplete_period_detail.week"}}
          {{else if (eq @aggregationPeriod "month")}}
            {{t "general.incomplete_period_detail.month"}}
          {{/if}}

          {{t "general.incomplete_period_extra"}}
        </div>
      </div>
    {{/if}}

    {{#if this.access.hasEvents}}
      <GeneralTooltipsTooltipEvents
        @events={{this.events}}
        @holidays={{this.holidays}}
        @title={{tKey "events.events"}}
      />
    {{/if}}
  </template>
}
